import React, { useState } from "react";
import { StyleSheet, View } from "react-native";
import { colors, layout } from "@/constants";
import Card from "./base/Card";
import Text from "./base/Text";
import ItemContextMenu from "./base/ItemContextMenu";
import ConfirmDeleteDialog from "./base/ConfirmDeleteDialog";

/**
 * Props for the FactCard component
 */
interface FactCardProps {
  /** The fact label (e.g., "preferredName") */
  label: string;
  /** The fact value */
  value: string;
  /** Callback when user deletes this fact */
  onDelete?: () => void;
}

/**
 * Formats a camelCase fact key to Title Case label
 * @param key - The camelCase key (e.g., "preferredName")
 * @returns Formatted label (e.g., "Preferred Name")
 */
const formatFactLabel = (key: string): string => {
  // Insert space before uppercase letters and capitalize first letter
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const formatFactValue = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Card component for displaying a single fact
 * Shows label and value in a simple layout
 *
 * @param props - The component props
 * @returns A styled fact card
 */
export default function FactCard({ label, value, onDelete }: FactCardProps) {
  const [confirmVisible, setConfirmVisible] = useState(false);
  const formattedLabel = formatFactLabel(label);
  const formattedValue = formatFactValue(value);

  return (
    <Card>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text variant="medium" style={styles.label}>
            {formattedLabel}
          </Text>
          {onDelete && (
            <ItemContextMenu
              items={[
                {
                  icon: "delete",
                  label: "Delete",
                  variant: "danger",
                  onPress: () => setConfirmVisible(true)
                }
              ]}
            />
          )}
        </View>
        <Text color="primary">{formattedValue}</Text>
      </View>

      <ConfirmDeleteDialog
        visible={confirmVisible}
        title="Delete fact?"
        message={`This will remove "${formattedLabel}" from your facts. If you mention it again in a future session, it may be re-extracted.`}
        onConfirm={() => {
          setConfirmVisible(false);
          onDelete?.();
        }}
        onCancel={() => setConfirmVisible(false)}
      />
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: layout.space.xs
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  label: {
    flex: 1,
    color: colors.onAiContainer
  }
});
